import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** Result of the token refresh that is currently in flight. */
export type RefreshOutcome =
  | { status: 'pending' }
  | { status: 'success'; token: string }
  | { status: 'failed'; error: unknown };

/** Coordinates the single token refresh shared by every request that hits a 401.
 *
 *  The outcome must always settle. Requests that arrive mid-refresh wait for it,
 *  and a wait that never ends leaves those requests unfinished - which keeps the
 *  global loader overlay up and swallows every click until the page is reloaded.
 *  Hence `fail()` is also called from `finalize`, covering the case where the
 *  refresh is torn down without succeeding or erroring. */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private refreshing = false;
  readonly outcome = new BehaviorSubject<RefreshOutcome>({ status: 'pending' });

  get isRefreshing(): boolean {
    return this.refreshing;
  }

  start(): void {
    this.refreshing = true;
    this.outcome.next({ status: 'pending' });
  }

  succeed(token: string): void {
    this.refreshing = false;
    this.outcome.next({ status: 'success', token });
  }

  /** No-op once the refresh has already settled, so a late `finalize` cannot
   *  turn a successful refresh into a failed one. */
  fail(error: unknown): void {
    this.refreshing = false;
    if (this.outcome.value.status === 'pending') {
      this.outcome.next({ status: 'failed', error });
    }
  }
}
